// ./dialogs/CreditDialog.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import MobileDialog from './MobileDialog';
import API from '../../services/api';
import { useQuery } from '@tanstack/react-query';

// Fetch clients with server-side search
async function fetchClientsAutocomplete(searchTerm = '') {
  const params = {
    light: true,
    limit: 100
  };
  if (searchTerm) {
    params.search = searchTerm;
  }
  const response = await API.get('/clients/', { params });
  return response.data;
}

const CreditDialog = ({
  open,
  onClose,
  debtFields = [],
  debtFieldsSchema = {},
  fetchTransactions,
  invalidateClientListCache,
  userProfile,
  initialClient = null,
}) => {
  // Client selection
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDebt, setClientDebt] = useState({});

  // Server-side autocomplete search state
  const [debouncedAutocompleteSearch, setDebouncedAutocompleteSearch] = useState('');
  const autocompleteTimerRef = useRef(null);

  // React Query for server-side client search
  const { data: autocompleteClients = [], isLoading: isLoadingAutocomplete } = useQuery({
    queryKey: ['clients', 'autocomplete', 'credit', debouncedAutocompleteSearch],
    queryFn: () => fetchClientsAutocomplete(debouncedAutocompleteSearch),
    enabled: open,  // Only fetch when dialog is open
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Create options with "no results" indicator when empty and user is searching
  const autocompleteOptions = useMemo(() => {
    if (autocompleteClients.length === 0 && debouncedAutocompleteSearch) {
      return [{ ClientBusinessID: -1, ClientName: 'אין תוצאות', isNoResults: true }];
    }
    return autocompleteClients;
  }, [autocompleteClients, debouncedAutocompleteSearch]);

  // Debounced callback for autocomplete search
  const debouncedSetAutocompleteSearch = useCallback((value) => {
    if (autocompleteTimerRef.current) {
      clearTimeout(autocompleteTimerRef.current);
    }
    autocompleteTimerRef.current = setTimeout(() => {
      setDebouncedAutocompleteSearch(value);
    }, 300);
  }, []);

  // Check if there's only one category (excluding TotalDebt)
  const singleCategory = useMemo(() => {
    const creditCategories = debtFields.filter(field => field !== 'TotalDebt');
    return creditCategories.length === 1 ? creditCategories[0] : null;
  }, [debtFields]);
  
  // Credit details
  const [amount, setAmount] = useState('');
  const [field, setField] = useState(singleCategory || '');
  const [invoice, setInvoice] = useState('');
  const [notes, setNotes] = useState('');
  const [actualDate, setActualDate] = useState(new Date());
  
  // Extra fields state
  const [extraFieldValues, setExtraFieldValues] = useState({});
  
  // Form state
  const [formStep, setFormStep] = useState('form');
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const errorRef = useRef(null);

  // Scroll to error when it appears
  useEffect(() => {
    if (localError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [localError]);

  // Memoize configuration values to prevent infinite render loops
  const uiConfig = useMemo(() => userProfile?.ui_config || {}, [userProfile]);
  const transactionFields = useMemo(() => uiConfig.transaction_fields || [], [uiConfig]);
  const transactionFieldsSchema = useMemo(() => uiConfig.transaction_fields_schema || {}, [uiConfig]);
  
  // Check if Invoice field is available
  const hasInvoiceField = useMemo(() => transactionFields.includes('Invoice'), [transactionFields]);
  
  // Standard fields that are always part of transactions
  const standardFields = useMemo(() => 
    ['TrID', 'Type', 'SubType', 'Amount', 'TotalAfterTR', 'Timestamp', 'Invoice', 'Note'], 
    []
  );
  
  // Extract extra fields by comparing to standard fields - memoize to prevent re-renders
  const extraFields = useMemo(() => 
    transactionFields.filter(field => 
      !standardFields.includes(field) && field !== 'TrID' && field !== 'Timestamp'
    ),
    [transactionFields, standardFields]
  );

  // Initialize extra fields with empty values only once when extraFields changes
  useEffect(() => {
    if (extraFields && extraFields.length > 0) {
      setExtraFieldValues(prev => {
        // Skip update if extraFields is already initialized
        if (Object.keys(prev).length === extraFields.length && 
            extraFields.every(field => field in prev)) {
          return prev;
        }
        
        const initialExtraValues = {};
        extraFields.forEach(field => {
          initialExtraValues[field] = prev[field] || '';
        });
        return initialExtraValues;
      });
    }
  }, [extraFields]);

  // Update field value when singleCategory changes
  useEffect(() => {
    if (singleCategory) {
      setField(singleCategory);
    }
  }, [singleCategory]);

  // Pre-select client when initialClient is provided and dialog opens
  useEffect(() => {
    if (open && initialClient && !selectedClient) {
      setSelectedClient(initialClient);
    }
  }, [open, initialClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch client debt when client is selected
  useEffect(() => {
    const loadDebt = async () => {
      if (!selectedClient) {
        setClientDebt({});
        return;
      }
      try {
        const resp = await API.get(
          `/client?business_id=${selectedClient.ClientBusinessID}`
        );
        setClientDebt(resp.data.debt?.DebtFields || {});
      } catch (err) {
        console.error('Error fetching client debt for Credit:', err);
      }
    };
    loadDebt();
  }, [selectedClient]);

  // Reset form state
  const resetForm = () => {
    setSelectedClient(null);
    setAmount('');
    setField(singleCategory || '');
    setInvoice('');
    setNotes('');
    setActualDate(new Date());

    // Reset extra field values
    const initialExtraValues = {};
    extraFields.forEach(field => {
      initialExtraValues[field] = '';
    });
    setExtraFieldValues(initialExtraValues);

    setFormStep('form');
    setLocalError('');
    setLocalSuccess('');
    setClientDebt({});

    // Reset autocomplete search state
    setDebouncedAutocompleteSearch('');
    if (autocompleteTimerRef.current) {
      clearTimeout(autocompleteTimerRef.current);
    }
  };

  // Handle form close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Update an extra field value
  const handleExtraFieldChange = (field, value) => {
    setExtraFieldValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Check if field is read-only
  const isFieldReadOnly = (fieldName) => {
    const schema = transactionFieldsSchema[fieldName] || {};
    return schema.readonly === true;
  };

  // Validate form before moving to confirmation step
  const handleValidation = () => {
    setLocalError('');
    setLocalSuccess('');
    
    if (!selectedClient) {
      setLocalError('שגיאה: יש לבחור לקוח לפני זיכוי.');
      return;
    }
    
    const parsedAmount = parseFloat(amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setLocalError('שגיאה: סכום הזיכוי חייב להיות גדול מאפס.');
      return;
    }
    
    if (!field) {
      setLocalError('שגיאה: יש לבחור קטגוריה לזיכוי.');
      return;
    }
    
    setFormStep('confirm');
  };

  // Submit credit
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setLocalError('');
    setLocalSuccess('');
    
    try {
      const parsedAmount = parseFloat(amount);
      // Create the basic payload
      const payload = {
        business_id: selectedClient.ClientBusinessID,
        amount: parsedAmount,
        field: field,
        notes: notes,
        invoice: invoice,
      };

      // Add actual_date if set
      if (actualDate) {
        payload.actual_date = actualDate.toISOString();
      }

      // Add extra fields to the payload
      extraFields.forEach(field => {
        if (extraFieldValues[field] !== '') {
          payload[field] = extraFieldValues[field];
        }
      });

      await API.post('/client/credit/', payload);
      setLocalSuccess('זיכוי בוצע בהצלחה!');
      
      if (invalidateClientListCache) {
        invalidateClientListCache();
      }
      
      if (fetchTransactions) {
        fetchTransactions();
      }
      
      // Close dialog after delay
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('שגיאה בזיכוי:', err);
      setLocalError(
        err?.response?.data?.error ||
        err.message ||
        'שגיאה בעת הזיכוי'
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <MobileDialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.4rem' }}>
        טופס זיכוי
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {localError && (
            <Alert ref={errorRef} severity="error">{localError}</Alert>
          )}
          {localSuccess && (
            <Alert severity="success">{localSuccess}</Alert>
          )}

          {formStep === 'form' && (
            <>
              <Autocomplete
                options={autocompleteOptions}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') return option;
                  if (option.isNoResults) return option.ClientName;
                  return option.ClientName
                    ? `${option.ClientName} (${option.ClientBusinessID})`
                    : '';
                }}
                getOptionDisabled={(option) => option.isNoResults === true}
                filterOptions={(x) => x}  // Disable client-side filtering (server does it)
                loading={isLoadingAutocomplete}
                loadingText="טוען..."
                openOnFocus
                value={selectedClient}
                onInputChange={(event, newInputValue, reason) => {
                  // Trigger debounced server-side search
                  debouncedSetAutocompleteSearch(newInputValue);
                }}
                onChange={(event, newValue) => {
                  // Ignore "no results" dummy option
                  if (newValue && typeof newValue === 'object' && newValue.isNoResults) {
                    return;
                  }
                  setSelectedClient(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="בחר לקוח (תז/חפ/שם)"
                    variant="outlined"
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {isLoadingAutocomplete ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <TextField
                label="סכום זיכוי"
                type="number"
                sx={{
                  direction: 'ltr',
                  '& input': {
                    textAlign: 'center',
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none',
                      margin: 0,
                    },
                    '-moz-appearance': 'textfield',
                  },
                }}
                fullWidth
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onWheel={(e) => e.target.blur()}
                onFocus={(e) => e.target.select()}
              />
              
              {/* Only show invoice field if Invoice is in transaction fields */}
              {hasInvoiceField && (
                <TextField
                  label="אסמכתא/חשבונית (אופציונלי)"
                  type="text"
                  fullWidth
                  inputProps={{
                    style: { textAlign: 'right', direction: 'ltr' },
                  }}
                  value={invoice}
                  onChange={(e) => setInvoice(e.target.value)}
                />
              )}
              
              {/* Only show category selection when there are multiple categories */}
              {!singleCategory && (
                <TextField
                  select
                  label="קטגוריה לזיכוי"
                  fullWidth
                  value={field}
                  onChange={(e) => setField(e.target.value)}
                >
                  {/* Dynamically create menu items based on debt fields */}
                  {debtFields
                    .filter(field => field !== 'TotalDebt')
                    .map(field => (
                      <MenuItem key={field} value={field}>
                        {debtFieldsSchema[field]?.label || field}
                      </MenuItem>
                    ))
                  }
                </TextField>
              )}
              
              <TextField
                label="הערות (אופציונלי)"
                type="text"
                multiline
                minRows={2}
                fullWidth
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />

              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
                <DatePicker
                  label="תאריך בפועל"
                  value={actualDate}
                  onChange={(newValue) => setActualDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: 'התאריך בו בוצעה הפעולה בפועל (ברירת מחדל: היום)',
                    }
                  }}
                />
              </LocalizationProvider>

              {/* Render extra fields if configured */}
              {extraFields.length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    שדות נוספים
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {extraFields.map((fieldName) => {
                      const schema = transactionFieldsSchema[fieldName] || {};
                      const isReadOnly = isFieldReadOnly(fieldName);
                      const fieldType = schema.type === 'number' ? 'number' : 'text';
                      if (isReadOnly) {
                        return null;
                      }
                      return (
                        <Box sx={{ width: { xs: '100%', sm: '48%' } }} key={fieldName}>
                          <TextField
                            label={schema.label || fieldName}
                            type={fieldType}
                            value={extraFieldValues[fieldName] || ''}
                            onChange={(e) => handleExtraFieldChange(fieldName, e.target.value)}
                            onFocus={(e) => e.target.select()} 
                            fullWidth
                            disabled={isReadOnly}
                            InputProps={{
                              readOnly: isReadOnly,
                            }}
                            sx={{
                              direction: fieldType === 'number' ? 'ltr' : 'rtl',
                              '& input': {
                                textAlign: 'right',
                                '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                  WebkitAppearance: 'none',
                                  margin: 0,
                                },
                                '-moz-appearance': 'textfield',
                              },
                            }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">
                  חובות נוכחיים:
                </Typography>
                {/* Dynamically display current debt values */}
                {debtFields
                  .filter(field => field !== 'TotalDebt')
                  .map(field => (
                    <Typography key={field} variant="body2" color="text.secondary">
                      {debtFieldsSchema[field]?.label || field} נוכחי: {clientDebt[field] || 0}
                    </Typography>
                  ))
                }
              </Box>
            </>
          )}

          {formStep === 'confirm' && (
            <>
              <Typography
                sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 1 }}
              >
                אישור זיכוי
              </Typography>
              <Typography sx={{ fontWeight: 'bold' }}>
                ללקוח: {selectedClient?.ClientName} (
                {selectedClient?.ClientBusinessID})
              </Typography>
              <Typography>סכום זיכוי: {amount}</Typography>
              {hasInvoiceField && invoice && (
                <Typography>חשבונית: {invoice}</Typography>
              )}
              {notes && (
                <Typography>הערות: {notes}</Typography>
              )}
              {actualDate && (
                <Typography>תאריך בפועל: {actualDate.toLocaleDateString('he-IL')}</Typography>
              )}

              {/* Only show category in confirmation when there are multiple categories */}
              {!singleCategory && (
                <Typography sx={{ fontWeight: 'bold', mt: 2 }}>
                  קטגוריה: {debtFieldsSchema[field]?.label || field}
                </Typography>
              )}
              
              {/* Show extra fields in confirmation if they have values */}
              {extraFields.length > 0 && extraFields.some(fieldName => extraFieldValues[fieldName]) && (
                <>
                  <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                    שדות נוספים:
                  </Typography>
                  {extraFields.map(fieldName => 
                    extraFieldValues[fieldName] ? (
                      <Typography key={fieldName}>
                        {transactionFieldsSchema[fieldName]?.label || fieldName}: {extraFieldValues[fieldName]}
                      </Typography>
                    ) : null
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {!localSuccess && (
          <Button
            onClick={handleClose}
            color="inherit"
            size="large"
            sx={{ fontSize: '1rem' }}
          >
            ביטול
          </Button>
        )}
        
        {formStep === 'form' && !localSuccess && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ fontSize: '1rem' }}
            onClick={handleValidation}
          >
            שלב הבא
          </Button>
        )}
        
        {formStep === 'confirm' && !localSuccess && (
          <>
            <Button
              color="inherit"
              size="large"
              sx={{ fontSize: '1rem' }}
              onClick={() => setFormStep('form')}
            >
              חזרה
            </Button>
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ fontSize: '1rem' }}
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} />
              ) : (
                'אישור סופי'
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </MobileDialog>
  );
};

export default CreditDialog;